import { ChevronIcon } from "@theme/assets";
import { Fragment, useEffect, useRef, useState } from "react";

import { Box, MsButton, NavProductList } from "@theme/components";

import { NavShopAllLink } from "@theme/components/Navigation";

import type { NavCategory } from "@theme/types";
import type { FC } from "react";

interface NavMobileCategoryProps {
  navCategory: NavCategory | null;
  closeDrawer: () => void;
}

export const NavMobileCategory: FC<NavMobileCategoryProps> = ({ navCategory, closeDrawer }) => {
  const columns = navCategory?.navColumns;

  const firstSubCategoryId = columns?.[0]?.items[0].id;

  const [activeIndexes, setActiveIndexes] = useState<Array<string>>([]);

  const handleAccordionToggle = (id: string) => {
    const isActive = activeIndexes.includes(id);
    if (isActive) {
      setActiveIndexes(activeIndexes.filter((activeIndex) => activeIndex !== id));

      return;
    }

    setActiveIndexes([...activeIndexes, id]);
  };

  const backButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // There is a bug on mobile devices, where focusing will cause the rest of the elements
    // not to render properly with AnimatePresence when we try to focus.
    // Using onAnimationComplete to focus on the back button after elements properly render.
    if (backButtonRef.current) {
      backButtonRef.current.focus();
    }
    // Always open the first sub category when screen opens
    if (firstSubCategoryId) {
      setActiveIndexes([firstSubCategoryId]);
    }
  }, [firstSubCategoryId]);

  return (
    <>
      {navCategory && (
        <>
          <NavShopAllLink
            to={`${navCategory.pageSlug}`}
            onClick={closeDrawer}
            text={navCategory.shopAllCTAText}
            ariaLabel={navCategory.shopAllCTAText}
          />
          <Box data-testid={`mobile-${navCategory.slug}-subcategories`} sx={{ mt: "s500" }}>
            {columns?.map(
              (column, columnIndex) =>
                column.items.length > 0 && (
                  <Box key={columnIndex}>
                    {column.items.map((subCategory) => {
                      const isActive = activeIndexes.includes(subCategory.id);

                      return (
                        <Fragment key={subCategory.id}>
                          <MsButton
                            data-testid={`mobile-nav-${subCategory.slug}`}
                            variant="link"
                            sx={{
                              mb: "s500",
                              p: 0,
                              width: "full",
                              span: {
                                justifyContent: "space-between",
                                span: {
                                  fontSize: "fs18",
                                  color: "grey700",
                                  lineHeight: "lh400",
                                  textTransform: "capitalize",
                                },
                                svg: {
                                  transform: isActive ? "rotate(270deg)" : "rotate(90deg)",
                                  height: "14px",
                                },
                              },
                            }}
                            onClick={() => handleAccordionToggle(subCategory.id)}
                            tabIndex={0}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                handleAccordionToggle(subCategory.id);
                              }
                            }}
                            aria-label={`${subCategory?.title} accordion content`}
                            aria-expanded={isActive}
                            aria-controls={`accordion-content-${subCategory.id}`}
                            TrailingIcon={ChevronIcon}
                            iconColor="Gold"
                          >
                            {subCategory?.title}
                          </MsButton>

                          <Box
                            id={`accordion-content-${subCategory.id}`}
                            aria-hidden={!isActive}
                            sx={{
                              display: isActive ? "block" : "none",
                              pl: ["s700", null, null, 0],
                            }}
                          >
                            <NavProductList
                              navCategory={navCategory}
                              subCategory={subCategory}
                              closeNavigation={closeDrawer}
                            />
                          </Box>
                        </Fragment>
                      );
                    })}
                  </Box>
                ),
            )}
          </Box>
        </>
      )}
    </>
  );
};
